import React, { useState } from "react";
import { api, fmtAgo } from "../../lib/api";
import { Avatar, ConfirmModal, ReplaIdModal } from "../common";
import { EditCharacterModal } from "./CreationModals";
import { Plus, Pencil, Trash2, Search, RefreshCw } from "lucide-react";

export default function CharacterManager({ characters, onReload, onNew, online = [] }) {
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [idEditing, setIdEditing] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("name");

  async function confirmRemove() {
    await api.delete(`/characters/${toDelete.id}`);
    setToDelete(null);
    onReload?.();
  }

  const list = characters
    .filter((c) => c.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => {
      if (sort === "online") {
        const oa = a.user_id && online.includes(a.user_id) ? 0 : 1;
        const ob = b.user_id && online.includes(b.user_id) ? 0 : 1;
        if (oa !== ob) return oa - ob;
      }
      return (a.name || "").localeCompare(b.name || "");
    });

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ background: "var(--r-void)" }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="sys-label">RÉPERTOIRE</div>
            <h2 className="font-title font-bold text-xl tracking-tight mt-1">PERSONNAGES JOUEURS · {characters.length}</h2>
          </div>
          <button data-testid="charmanager-new" className="btn btn-solid flex items-center gap-2" onClick={onNew}><Plus size={14} /> NOUVEAU PJ</button>
        </div>
        <div className="flex gap-2 mb-4 flex-wrap">
          <div className="relative flex-1" style={{ minWidth: 200, maxWidth: 320 }}>
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--r-text-muted)] pointer-events-none" />
            <input data-testid="char-search" className="input font-mono text-xs" style={{ paddingLeft: 36 }} placeholder="RECHERCHER UN PJ" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="char-sort" className="input font-mono text-xs" style={{ width: "auto" }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="name">TRI : NOM</option>
            <option value="online">TRI : STATUT EN LIGNE</option>
          </select>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((c) => {
            const isOnline = c.user_id && online.includes(c.user_id);
            return (
              <div key={c.id} data-testid={`char-card-${c.id}`} className="frame p-3">
                <div className="flex items-start gap-3">
                  <div className="relative">
                    <Avatar src={c.avatar} name={c.name} size={44} />
                    {c.user_id && (
                      <span data-testid={`char-status-${c.id}`} title={isOnline ? "En ligne" : "Hors ligne"}
                        className="absolute -bottom-0.5 -right-0.5 rounded-full"
                        style={{ width: 12, height: 12, border: "2px solid var(--r-panel)", background: isOnline ? "#22C55E" : "var(--r-text-muted)", boxShadow: isOnline ? "0 0 6px #22C55E" : "none" }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold break-words leading-tight">{c.name}</div>
                    <div className="sys-label break-words leading-tight mt-0.5" style={{ textTransform: "none", letterSpacing: "normal" }}>
                      {c.player_email || "— aucun compte —"}
                    </div>
                    <div className="sys-label mt-0.5" style={{ color: isOnline ? "#22C55E" : "var(--r-text-muted)", textTransform: "none", letterSpacing: "normal" }}>
                      {c.user_id ? (isOnline ? "● EN LIGNE" : `○ HORS LIGNE · vu ${fmtAgo(c.last_seen)}`) : ""}
                    </div>
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-1.5">
                    <span className="sys-label" style={{ width: 90 }}>ID RÉPLA</span>
                    <span data-testid={`char-repla-id-${c.id}`} className="font-mono text-xs px-1.5 py-0.5" style={{ color: "var(--r-primary)", border: "1px solid color-mix(in srgb, var(--r-primary) 30%, transparent)" }}>{c.repla_id || "—"}</span>
                    <button data-testid={`regen-char-id-${c.id}`} title="Personnaliser / régénérer l'ID" onClick={() => setIdEditing(c)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-primary)]"><RefreshCw size={11} /></button>
                  </div>
                  {c.user_id && <div className="sys-label" style={{ textTransform: "none", letterSpacing: "normal", color: "var(--r-text-muted)" }}>↳ sert aussi d'identifiant de connexion</div>}
                </div>
                <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-[color:var(--r-primary)]/10">
                  <button data-testid={`edit-char-${c.id}`} onClick={() => setEditing(c)} className="btn btn-ghost flex-1 justify-center flex items-center gap-1 text-[10px] whitespace-nowrap" style={{ padding: "6px 8px" }}><Pencil size={11} /> MODIFIER</button>
                  <button data-testid={`del-char-${c.id}`} onClick={() => setToDelete(c)} className="btn btn-ghost flex-1 justify-center flex items-center gap-1 text-[10px] whitespace-nowrap" style={{ padding: "6px 8px", color: "var(--r-alert)" }}><Trash2 size={11} /> SUPPRIMER</button>
                </div>
              </div>
            );
          })}
        </div>
        {list.length === 0 && <div className="sys-label">AUCUN PERSONNAGE</div>}
      </div>

      <EditCharacterModal open={!!editing} onClose={() => setEditing(null)} character={editing} onSaved={onReload} />
      <ReplaIdModal
        open={!!idEditing}
        current={idEditing?.repla_id}
        onClose={() => setIdEditing(null)}
        onSubmit={async (val) => { await api.post(`/characters/${idEditing.id}/regenerate-id`, { repla_id: val }); onReload?.(); }}
      />
      <ConfirmModal
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmRemove}
        title="SUPPRIMER LE PERSONNAGE"
        confirmLabel="SUPPRIMER"
        message={toDelete ? `Supprimer le personnage « ${toDelete.name} » ? Il sera retiré de toutes ses conversations. Le compte joueur associé n'est pas supprimé.` : ""}
      />
    </div>
  );
}
